import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import supabase from '../../utils/supabaseClient';
import AdminNavBar from '../../components/AdminNavBar';
import './styles.css';
import './UploadImages.css';

const compressAndConvertToWebP = async (file, maxWidth = 1200, maxHeight = 1200, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = Math.round(height * maxWidth / width);
          width = maxWidth;
        }
        
        if (height > maxHeight) {
          width = Math.round(width * maxHeight / height);
          height = maxHeight;
        }
        
        // Create canvas and draw image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to WebP format
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Create a new file name with webp extension
              const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
              const newFileName = `${fileNameWithoutExt}.webp`;
              
              // Create a new file object
              const newFile = new File(
                [blob], 
                newFileName, 
                { type: 'image/webp', lastModified: new Date().getTime() }
              );
              
              // Add some metadata to help track compression results
              newFile.originalSize = file.size;
              newFile.compressedSize = blob.size;
              newFile.compressionRatio = ((1 - (blob.size / file.size)) * 100).toFixed(1);
              
              resolve(newFile);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          'image/webp',
          quality
        );
      };
      
      img.onerror = (error) => {
        reject(new Error('Error loading image'));
      };
    };
    
    reader.onerror = (error) => {
      reject(new Error('Error reading file'));
    };
  });
};

function UploadImages({ isPopup = false, onClose, onUploadComplete }) {
  const [currentFolder, setCurrentFolder] = useState('highres');
  const [folderHistory, setFolderHistory] = useState(['highres']);
  const [availableImages, setAvailableImages] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [message, setMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const imagesPerPage = 12; // Show more images per page in this dedicated view
  const [search, setSearch] = useState('');
  const [uploadProgress, setUploadProgress] = useState({});

  // Fetch images from the current folder
  const fetchAvailableImages = async () => {
    try {
      setMessage('');
      const { data: { publicUrl } } = supabase.storage
        .from('productsamples')
        .getPublicUrl('');
      
      const { data, error } = await supabase
        .storage
        .from('productsamples')
        .list(currentFolder, {
          sortBy: { column: 'name', order: 'asc' }
        });
      
      if (error) {
        console.error(`Error fetching from ${currentFolder}:`, error);
        setMessage(`Error al cargar imágenes: ${error.message}`);
        return;
      }
      
      if (!data || data.length === 0) {
        setAvailableImages([]);
        return;
      }
      
      // Process items with proper folder detection
      const formattedItems = data.map(item => {
        const isFolder = item.metadata === null || 
                         item.id === undefined ||
                         !item.name.includes('.');
        
        if (isFolder) {
          return {
            name: item.name,
            path: `${currentFolder}/${item.name}`,
            isFolder: true
          };
        } else {
          const path = `${currentFolder}/${item.name}`;
          return {
            name: item.name,
            path: path,
            url: `${publicUrl}${path}`,
            isFolder: false,
            size: item.metadata?.size || 0,
            lastModified: item.metadata?.lastModified || null
          };
        }
      });
      
      // Sort folders first, then files
      const sortedItems = formattedItems.sort((a, b) => {
        if (a.isFolder && !b.isFolder) return -1;
        if (!a.isFolder && b.isFolder) return 1;
        return a.name.localeCompare(b.name);
      });
      
      setAvailableImages(sortedItems);
      
      // Call onUploadComplete if it exists and we're refreshing after an upload
      // Only call it after explicit upload actions
      /*
      if (onUploadComplete) {
        onUploadComplete();
      }
      */
      
    } catch (err) {
      console.error('Error in fetchAvailableImages:', err);
      setMessage(`Error: ${err.message}`);
    }
  };

  useEffect(() => {
    fetchAvailableImages();
  }, [currentFolder]);

  const navigateToFolder = (folderPath) => {
    setFolderHistory(prev => [...prev, folderPath]);
    setCurrentFolder(folderPath);
    setCurrentPage(1); // Reset to first page when changing folders
  };

  const navigateBack = () => {
    if (folderHistory.length <= 1) return;
    
    const newHistory = [...folderHistory];
    newHistory.pop(); // Remove current folder
    const previousFolder = newHistory[newHistory.length - 1];
    
    setFolderHistory(newHistory);
    setCurrentFolder(previousFolder);
    setCurrentPage(1); // Reset to first page when going back
  };

  const handleFileChange = (e) => {
    setUploadedFiles(Array.from(e.target.files));
    
    // Add preview of how many MB will be saved
    const totalSize = Array.from(e.target.files).reduce((sum, file) => sum + file.size, 0);
    const estimatedSavedMB = ((totalSize * 0.6) / (1024 * 1024)).toFixed(2); // Estimate 60% reduction
    
    if (e.target.files.length > 0) {
      setMessage(`${e.target.files.length} archivos seleccionados. Tamaño original: ${(totalSize / (1024 * 1024)).toFixed(2)} MB. Ahorro estimado: ~${estimatedSavedMB} MB con compresión`);
    }
  };

  const handleUpload = async () => {
    if (uploadedFiles.length === 0) {
      setMessage('Por favor seleccione archivos para subir.');
      return;
    }

    setUploading(true);
    setMessage('Comprimiendo y convirtiendo imágenes...');
    
    try {
      const totalFiles = uploadedFiles.length;
      let successCount = 0;
      let errorCount = 0;
      let totalOriginalSize = 0;
      let totalCompressedSize = 0;
      
      // Process each file
      for (let i = 0; i < totalFiles; i++) {
        const originalFile = uploadedFiles[i];
        totalOriginalSize += originalFile.size;
        
        // Skip non-image files
        if (!originalFile.type.startsWith('image/')) {
          setUploadProgress(prev => ({
            ...prev,
            [originalFile.name]: {
              progress: 100,
              status: 'error',
              message: 'No es un archivo de imagen'
            }
          }));
          errorCount++;
          continue;
        }
        
        try {
          // Update progress - compression phase
          setUploadProgress(prev => ({
            ...prev,
            [originalFile.name]: {
              progress: 10,
              status: 'uploading',
              message: 'Comprimiendo...'
            }
          }));
          
          // Compress and convert the file
          const compressedFile = await compressAndConvertToWebP(originalFile);
          totalCompressedSize += compressedFile.size;
          
          // Build the file path with the new WebP extension
          const fileNameWithoutExt = originalFile.name.replace(/\.[^/.]+$/, "");
          const safeFileName = sanitizeFileName(fileNameWithoutExt);
          const filePath = `${currentFolder}/${safeFileName}.webp`;
          
          // Update progress - upload phase
          setUploadProgress(prev => ({
            ...prev,
            [originalFile.name]: {
              progress: 30,
              status: 'uploading',
              message: `Comprimido: -${compressedFile.compressionRatio}%, Subiendo...`
            }
          }));
          
          // Create a simulated upload progress
          const progressInterval = setInterval(() => {
            setUploadProgress(prev => {
              const currentProgress = prev[originalFile.name]?.progress || 30;
              if (currentProgress < 90) {
                return {
                  ...prev,
                  [originalFile.name]: {
                    ...prev[originalFile.name],
                    progress: currentProgress + 5
                  }
                };
              }
              return prev;
            });
          }, 200);
          
          // Upload the compressed file
          const { error } = await supabase.storage
            .from('productsamples')
            .upload(filePath, compressedFile, {
              cacheControl: '3600',
              upsert: true
            });
          
          clearInterval(progressInterval);
          
          if (error) {
            console.error(`Error uploading ${compressedFile.name}:`, error);
            errorCount++;
            setUploadProgress(prev => ({
              ...prev,
              [originalFile.name]: {
                progress: 100,
                status: 'error',
                message: error.message
              }
            }));
          } else {
            successCount++;
            setUploadProgress(prev => ({
              ...prev,
              [originalFile.name]: {
                progress: 100,
                status: 'success',
                message: `Reducido de ${(originalFile.size / (1024 * 1024)).toFixed(2)}MB a ${(compressedFile.size / (1024 * 1024)).toFixed(2)}MB (-${compressedFile.compressionRatio}%)`
              }
            }));
          }
        } catch (err) {
          console.error(`Error processing ${originalFile.name}:`, err);
          errorCount++;
          setUploadProgress(prev => ({
            ...prev,
            [originalFile.name]: {
              progress: 100,
              status: 'error',
              message: `Error al comprimir: ${err.message}`
            }
          }));
        }
      }
      
      // Show completion message with compression stats
      if (errorCount === 0) {
        const savedMB = ((totalOriginalSize - totalCompressedSize) / (1024 * 1024)).toFixed(2);
        const savingPercentage = ((1 - (totalCompressedSize / totalOriginalSize)) * 100).toFixed(1);
        setMessage(`${successCount} archivos subidos correctamente. Espacio ahorrado: ${savedMB}MB (${savingPercentage}% de reducción)`);
      } else {
        setMessage(`${successCount} archivos subidos. ${errorCount} fallidos.`);
      }
      
      // Refresh the file list
      await fetchAvailableImages();
      
      // Clear the file input after a successful upload
      if (successCount > 0) {
        setUploadedFiles([]);
      }
      
    } catch (err) {
      console.error('Error in handleUpload:', err);
      setMessage(`Error al subir archivos: ${err.message}`);
    } finally {
      setUploading(false);
      
      // Keep progress visible longer to show compression results
      setTimeout(() => {
        setUploadProgress({});
      }, 5000);
    }
  };

  const createNewFolder = async () => {
    if (!newFolderName.trim()) {
      setMessage('El nombre de la carpeta no puede estar vacío');
      return;
    }
    
    try {
      // Create an empty file as a placeholder (Supabase doesn't have an explicit "create folder" API)
      const { error } = await supabase.storage
        .from('productsamples')
        .upload(`${currentFolder}/${newFolderName}/.placeholder`, new Blob([''], { 
          upsert: true,
          cacheControl: '3600'
        }));
      
      if (error) {
        console.error('Error creating folder:', error);
        setMessage(`Error al crear carpeta: ${error.message}`);
        return;
      }
      
      // Update UI
      setMessage('Carpeta creada correctamente');
      setNewFolderName('');
      setShowNewFolderInput(false);
      
      // Refresh the folder contents
      fetchAvailableImages();
      
    } catch (err) {
      console.error('Error in createNewFolder:', err);
      setMessage(`Error: ${err.message}`);
    }
  };
  
  const handleDeleteFile = async (path) => {
    if (!window.confirm('¿Está seguro que desea eliminar este archivo?')) {
      return;
    }
    
    try {
      const { error } = await supabase.storage
        .from('productsamples')
        .remove([path]);
      
      if (error) {
        console.error('Error deleting file:', error);
        setMessage(`Error al eliminar archivo: ${error.message}`);
        return;
      }
      
      setMessage('Archivo eliminado correctamente');
      fetchAvailableImages();
      
    } catch (err) {
      console.error('Error in handleDeleteFile:', err);
      setMessage(`Error: ${err.message}`);
    }
  };

  // Filter images by search term
  const filteredImages = availableImages.filter(item => 
    item && item.name && 
    item.name.toLowerCase().includes((search || '').toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filteredImages.length / imagesPerPage));

  const imagesToDisplay = filteredImages.slice(
    (currentPage - 1) * imagesPerPage,
    currentPage * imagesPerPage
  );

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Add this function
  const sanitizeFileName = (name) => {
    // First, replace common Spanish characters with ASCII equivalents
    const replacements = {
      'á': 'a', 'à': 'a', 'ä': 'a', 'â': 'a', 'ā': 'a', 'ã': 'a',
      'é': 'e', 'è': 'e', 'ë': 'e', 'ê': 'e', 'ē': 'e',
      'í': 'i', 'ì': 'i', 'ï': 'i', 'î': 'i', 'ī': 'i',
      'ó': 'o', 'ò': 'o', 'ö': 'o', 'ô': 'o', 'ō': 'o', 'õ': 'o',
      'ú': 'u', 'ù': 'u', 'ü': 'u', 'û': 'u', 'ū': 'u',
      'ñ': 'n',
      'ç': 'c',
      'Á': 'A', 'À': 'A', 'Ä': 'A', 'Â': 'A', 'Ā': 'A', 'Ã': 'A',
      'É': 'E', 'È': 'E', 'Ë': 'E', 'Ê': 'E', 'Ē': 'E',
      'Í': 'I', 'Ì': 'I', 'Ï': 'I', 'Î': 'I', 'Ī': 'I',
      'Ó': 'O', 'Ò': 'O', 'Ö': 'O', 'Ô': 'O', 'Ō': 'O', 'Õ': 'O',
      'Ú': 'U', 'Ù': 'U', 'Ü': 'U', 'Û': 'U', 'Ū': 'U',
      'Ñ': 'N',
      'Ç': 'C'
    };
    
    // Replace accented characters
    let sanitized = name;
    Object.keys(replacements).forEach(char => {
      sanitized = sanitized.replace(new RegExp(char, 'g'), replacements[char]);
    });
    
    // Remove any remaining non-ASCII characters and keep only safe ones
    sanitized = sanitized.replace(/[^a-zA-Z0-9\-_\.]/g, '_')
                        .replace(/\.+/g, '.') // Collapse multiple dots
                        .replace(/^\.+|\.+$/g, '') // Remove leading/trailing dots
                        .replace(/_+/g, '_') // Collapse multiple underscores
                        .replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
    
    return sanitized;
  };

  const isValidPath = (path) => {
    return path && path.trim() !== '' && !path.includes('//');
  };

  if (isPopup) {
    return (
      <div className="upload-page-popup">
        <h2>Gestión de Imágenes</h2>
        
        {message && (
          <div className={`message ${message.includes('Error') ? 'error-message' : 'success-message'}`}>
            {message}
          </div>
        )}
        
        <div className="upload-container">
          {/* Upload form part */}
          <div className="upload-form">
            <h3>Subir Imágenes</h3>
            <p>Carpeta actual: <strong>{currentFolder}</strong></p>
            
            <div className="file-input-container">
              <input 
                type="file" 
                id="file-upload-popup" 
                multiple 
                onChange={handleFileChange} 
                accept="image/*"
                disabled={uploading}
              />
              <label htmlFor="file-upload-popup" className={uploading ? "disabled" : ""}>
                Seleccionar Archivos
              </label>
              <span className="selected-count">
                {uploadedFiles.length > 0 ? `${uploadedFiles.length} archivo(s) seleccionado(s)` : 'Ningún archivo seleccionado'}
              </span>
            </div>
            
            <button 
              className="upload-button" 
              onClick={handleUpload}
              disabled={uploadedFiles.length === 0 || uploading}
            >
              {uploading ? 'Subiendo...' : 'Subir Archivos'}
            </button>
            
            {/* Upload progress */}
            {Object.keys(uploadProgress).length > 0 && (
              <div className="upload-progress-container">
                {Object.entries(uploadProgress).map(([fileName, data]) => (
                  <div key={fileName} className="upload-progress-item">
                    <div className="upload-progress-info">
                      <span className="upload-filename">{fileName}</span>
                      <span className="upload-status">
                        {data.status === 'uploading' && `${data.progress}%`}
                        {data.status === 'success' && '✓'}
                        {data.status === 'error' && '✗'}
                      </span>
                    </div>
                    <div className="upload-progress-bar">
                      <div 
                        className={`upload-progress-fill ${data.status === 'error' ? 'error' : ''}`}
                        style={{ width: `${data.progress}%` }}
                      ></div>
                    </div>
                    {data.message && (
                      <div className={`upload-message ${data.status === 'error' ? 'upload-error-message' : ''}`}>
                        {data.message}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Folder browser */}
        <div className="folder-browser">
          <div className="browser-header">
            <h2>Explorador de Archivos</h2>
            <div className="browser-actions">
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Buscar archivos..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="search-input"
                />
              </div>
              <button 
                className="new-folder-btn"
                onClick={() => setShowNewFolderInput(!showNewFolderInput)}
              >
                Nueva Carpeta
              </button>
            </div>
          </div>
          
          {showNewFolderInput && (
            <div className="new-folder-form">
              <input
                type="text"
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                placeholder="Nombre de la carpeta"
                className="folder-name-input"
              />
              <button 
                onClick={createNewFolder}
                disabled={!newFolderName.trim()}
                className="create-folder-btn"
              >
                Crear
              </button>
              <button 
                onClick={() => {
                  setShowNewFolderInput(false);
                  setNewFolderName('');
                }}
                className="cancel-btn"
              >
                Cancelar
              </button>
            </div>
          )}
          
          <div className="folder-navigation">
            <button 
              className="back-button" 
              onClick={navigateBack}
              disabled={folderHistory.length <= 1}
            >
              ⬅️ Atrás
            </button>
            <div className="breadcrumbs">
              {folderHistory.map((folder, index) => (
                <span key={index} className="breadcrumb-item">
                  {index > 0 && <span className="breadcrumb-separator">/</span>}
                  <span 
                    className="breadcrumb-text"
                    onClick={() => {
                      // Navigate to this folder
                      const newHistory = folderHistory.slice(0, index + 1);
                      setFolderHistory(newHistory);
                      setCurrentFolder(folder);
                    }}
                  >
                    {folder === 'highres' ? 'Raíz' : folder.split('/').pop()}
                  </span>
                </span>
              ))}
            </div>
          </div>
          
          {/* Refresh Images Button */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <button
              type="button"
              onClick={fetchAvailableImages}
              style={{
                padding: '8px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                marginBottom: '10px'
              }}
            >
              Refrescar Imágenes
            </button>
          </div>
          <div className="files-grid">
            {/* Only show valid image files and folders */}
            {imagesToDisplay.length > 0 ? (
              imagesToDisplay
                .filter(item => {
                  if (item.isFolder) return true;
                  const validImageExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
                  const lowerName = item.name?.toLowerCase() || '';
                  if (lowerName === '.placeholder') return false;
                  if (!validImageExtensions.some(ext => lowerName.endsWith(ext))) return false;
                  return true;
                })
                .map((item, index) => (
                  item.isFolder ? (
                    // Render folder
                    <div 
                      key={index}
                      className="folder-item"
                      onClick={() => navigateToFolder(item.path)}
                    >
                      <div className="folder-icon">📁</div>
                      <span className="folder-name">{item.name}</span>
                    </div>
                  ) : (
                    // Render file
                    <div key={index} className="file-item">
                      <div className="file-preview">
                        <img src={item.url} alt={item.name} />
                      </div>
                      <div className="file-info">
                        <div className="file-name">{item.name}</div>
                        {item.size && <div className="file-size">{formatFileSize(item.size)}</div>}
                      </div>
                      <div className="file-actions">
                        <button 
                          className="delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFile(item.path);
                          }}
                          title="Eliminar archivo"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  )
                ))
            ) : (
              <div className="empty-folder">
                {search ? 'No hay resultados que coincidan con tu búsqueda.' : 'No hay archivos en esta carpeta.'}
              </div>
            )}
          </div>
          
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                ← Anterior
              </button>
              <span className="page-indicator">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Siguiente →
              </button>
            </div>
          )}
        </div>
      </div>
    );
  } else {
    return (
      <div>
        <AdminNavBar />
        <div className="page-container upload-page">
          {/* Original code */}
          {/* ... */}
        </div>
      </div>
    );
  }
}

export default UploadImages;